import {logLoginEvent} from '@/common/analytics-events';
import {BookingFormButton} from '@/components/Buttons/Form/FormButton';
import {FormDividerRevised} from '@/components/Forms/FormDivider/FormDividerRevised';
import {EmailSignInForm} from '@/pages/SignInSignUp/EmailSignInForm';
import {useAppDispatch} from '@/redux/reduxUtils';
import {useQueryClient} from '@tanstack/react-query';
import {signInWithEmailAndPassword} from 'firebase/auth';
import {useState} from 'react';
import {Helmet} from 'react-helmet';
import {Link, useNavigate} from 'react-router-dom';
import AppleIcon from '../../assets/img/icons/redesign-icons/logo-apple.svg?react';
import FacebookIcon from '../../assets/img/icons/redesign-icons/logo-facebook.svg?react';
import GoogleIcon from '../../assets/img/icons/redesign-icons/logo-google.svg?react';
import MyTocaLogo from '../../assets/img/icons/redesign-icons/mytoca-logo.svg?react';
import {auth as fireBaseAuth, signInWithApple, signInWithFacebook, signInWithGoogle} from '../../firebase/config';
import {useSocialAuth} from '../../firebase/socialAuth.hooks';
import {useSetGlobalLoading} from '../../hooks/useGlobalLoading';
import {getPostLoginRedirectUri, setAccessToken, setCurrentAccountId} from '../../user/user-utils';
import styles from './SignInSignUp.module.scss';

export const SignInSignUp = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const socialAuth = useSocialAuth();
  const queryClient = useQueryClient();
  const {setGlobalLoading} = useSetGlobalLoading();
  const [errMes, setErrMes] = useState<string>('');

  const onEmailSubmit = async (data: any) => {
    setGlobalLoading(true);

    let userCredentials: any; // TODO typescript
    try {
      userCredentials = await signInWithEmailAndPassword(fireBaseAuth, data.email, data.password);
      logLoginEvent('emailAndPassword', {
        userUid: userCredentials?.user?.uid,
        operationType: userCredentials?.operationType,
      });
    } catch (err) {
      setGlobalLoading(false);
      setErrMes('Invalid username or password provided');
      return;
    }

    const authUser = userCredentials.user;

    setAccessToken(authUser.accessToken);
    setCurrentAccountId(authUser.uid);

    dispatch({type: 'GET_USER'});
    setGlobalLoading(false);
    navigate(authUser.emailVerified ? getPostLoginRedirectUri() : '/verify-email');
  };

  const onSocialSignIn = async (socialProvider: any) => {
    setGlobalLoading(true);
    const {success} = await socialAuth(socialProvider);
    queryClient.invalidateQueries({queryKey: ['user'], refetchType: 'all'});
    setGlobalLoading(false);
    if (success) {
      navigate(getPostLoginRedirectUri());
    }
  };

  return (
    <>
      <Helmet>
        <title>MyTOCA | Sign In/Sign Up</title>
      </Helmet>

      <div className={styles.container}>
        <div className={styles.logo}>
          <Link to={'/auth'}>
            <MyTocaLogo />
          </Link>
        </div>
        <h1 className={styles.heading}>Welcome</h1>
        <h2 className={styles.subheading}>Please Log in with your MyTOCA credentials</h2>

        <div className={styles.socialAuth}>
          <button onClick={() => onSocialSignIn(signInWithGoogle)} className={styles.socialButton}>
            <GoogleIcon />
          </button>
          <button onClick={() => onSocialSignIn(signInWithFacebook)} className={styles.socialButton}>
            <FacebookIcon />
          </button>
          <button onClick={() => onSocialSignIn(signInWithApple)} className={styles.socialButton}>
            <AppleIcon />
          </button>
        </div>

        <FormDividerRevised text="or" />

        <div className={styles.flexContainer}>
          <EmailSignInForm errorMessageProps={errMes} onSubmitHandler={onEmailSubmit} />

          <div className={styles.forgotPasswordLinkContainer}>
            <Link to="/forget-password">Forgot your password?</Link>
          </div>

          {import.meta.env.VITE_PHONE_ADD_SIGN_IN_ENABLED == 'true' && (
            <>
              <FormDividerRevised text="or" />

              <div className={styles.buttonWrapper}>
                <BookingFormButton onClick={() => navigate('/auth/signin/phone')}>
                  <p className={styles.buttonText}>Sign in with Phone Number</p>
                </BookingFormButton>
              </div>
            </>
          )}

          <FormDividerRevised text="or" />

          <Link to={'/signup'}>
            <div className={styles.buttonWrapper}>
              <BookingFormButton>
                <p className={styles.buttonText}>Create An Account</p>
              </BookingFormButton>
            </div>
          </Link>
        </div>

        <div className={styles.helpLinkContainer}>
          <Link to="/helpdesk">Need help?</Link>
        </div>
      </div>
    </>
  );
};
